import { useMemo } from 'react';
import { useGetSiteConfigs } from '@/hooks/query/use-site-configs';
import { SwiperContainer } from '@/components/containers/swiper-container';
import { siteService } from '@/services/site.service';
import { AnalyseKey, useConfigsAnalyseAttribute } from '@/features/next-analyse';
import { useBannerNavigation } from './hooks/use-banner-navigation';
import { useBannerList, type AdvertisementItem } from './hooks/use-banner-list';

type BannerCarouselSlideProps = {
	item: AdvertisementItem;
	imageServerUrl?: string;
	isPriority: boolean;
};

const BannerCarouselSlide = ({ item, imageServerUrl, isPriority }: BannerCarouselSlideProps) => {
	const { image, refinedAnalyseKey } = item;
	const analyseConfigs = useMemo(
		() => (refinedAnalyseKey ? [{ key: refinedAnalyseKey, analyseKey: AnalyseKey.Refined }] : []),
		[refinedAnalyseKey],
	);
	const refinedAnalyseRef = useConfigsAnalyseAttribute(analyseConfigs);

	const getImageSrc = (href: string) =>
		image.isFinalUrl || !image.useImageServer
			? href
			: `${siteService.getCombinedImagePathWithoutVersionId(
				href, 
				image.inGalaxyFolder ?? false, 
				imageServerUrl ?? ''
			)}?${siteService.getVersionId()}`;

	const picture = (
		<picture>
			<source
				type="image/webp" 
				srcSet={getImageSrc(image.webpHref)}
			/>
			<img
				src={getImageSrc(image.href)}
				loading={isPriority ? 'eager' : 'lazy'} 
				onError={(e) => {
					const target = e.currentTarget;
					if (target.dataset.fallbackApplied || !image.fallbackHref) return;
					target.dataset.fallbackApplied = 'true';
					const fallbackSrc = !image.useImageServer ? image.fallbackHref
						: `${siteService.getCombinedImagePathWithoutVersionId(image.fallbackHref, image.inGalaxyFolder ?? false, imageServerUrl ?? '')}?${siteService.getVersionId()}`;
					target.src = fallbackSrc;
					const sourceElement = target.parentElement?.querySelector('source');
					if (sourceElement && image.fallbackWebpHref) {
						const fallbackWebpSrc = !image.useImageServer ? image.fallbackWebpHref
							: `${siteService.getCombinedImagePathWithoutVersionId(image.fallbackWebpHref, image.inGalaxyFolder ?? false, imageServerUrl ?? '')}?${siteService.getVersionId()}`;
						sourceElement.srcset = fallbackWebpSrc;
					}
				}}
			/>
		</picture>
	);

	if (refinedAnalyseKey) {
		return (
			<div ref={refinedAnalyseRef} className="h-full w-full">
				{picture}
			</div>
		);
	}
	return picture;
};

export const Banners = () => {
	const { SiteConfigsData } = useGetSiteConfigs();
	const { navigate } = useBannerNavigation();
	const advertisementList = useBannerList();

	const onSlideClick = (index: number) => {
		const name = advertisementList[index].name;
		navigate(name);
	};

	return (
		<div className="home-banner">
			<SwiperContainer
				slideElements={advertisementList.map((advertisementItem, index) => (
					<BannerCarouselSlide
						key={advertisementItem.name}
						item={advertisementItem}
						imageServerUrl={SiteConfigsData?.ImageServerUrl}
						isPriority={index === 0}
					/>
				))}
				slideTrackTagNames={advertisementList.map(item => item.trackTagName)}
				className=""
				elementClassName=""
				onSlideClick={onSlideClick}
				customProps={{
					navigation: { enabled: false },
					pagination: { enabled: true },
					allowTouchMove: true,
					spaceBetween: 6,
					loop: true,
					autoplay: {
						delay: 2500,
						disableOnInteraction: false
					},
					breakpoints: {
						750: {
							navigation: { enabled: true },
							pagination: { enabled: false }
						}
					}
				}}
			/>
		</div>
	);
};